import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { isValidUrl } from '@makyoapp/makyo-common'

import { CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'
import { isEmpty, isFile } from '@/shared/utils'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { getFileExtensionAndSize, getFileSizeInBytes, validateFileTypes } from '../../../form-utils'
import { useUpdateFormDefaultValue } from '../../../hooks/useUpdateFormDefaultValue'
import { CompFileUploadProps } from './comp'

export async function fetchFileFromUrl(url: string): Promise<File> {
  const response = await fetch(url)
  const blob = await response.blob()
  const filename = url.split('/').pop() || 'default-file'
  return new File([blob], filename, { type: blob.type })
}

export interface CompFileUploadWrapperProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  children: (props: CompFileUploadProps) => React.ReactNode
}

/**
 * CompFileUploadWrapperCore
 */
export const CompFileUploadWrapperCore = ({
  infos: { settings, containerClient, detailTableId, detailRowIndex, currentParentBg, component },
  form,
  children,
}: CompFileUploadWrapperProps) => {
  const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // ========================= form settings =========================
  const { formState, setFormState, formErrors, setFormErrors, setRequiredFieldStatus } = form

  // ========================= data =========================
  const columnId = settings?.[ComponentSettingKey.FileUploadDataValue] as string

  const defaultValue = useParseVariableSelect<string>(
    settings?.[ComponentSettingKey.FileUploadDataDefaultValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const labelData = settings?.[ComponentSettingKey.FileUploadDataLabel]
  const label = useParseVariableText(labelData, containerClient, detailTableId, detailRowIndex)
  const helpTextData = settings?.[ComponentSettingKey.FileUploadDataHelp]
  const helpText = useParseVariableText(helpTextData, containerClient, detailTableId, detailRowIndex)

  // ========================= design =========================

  // ========================= behavior =========================
  const maxSize = getSafely(settings, [ComponentSettingKey.FileUploadBehaviorMaxSize], {
    value: 10,
    unit: 'mb',
  })
  const maxSizeValue = getFileSizeInBytes(maxSize.value, maxSize.unit)

  const acceptedFileTypes = getSafely(
    settings,
    [ComponentSettingKey.FileUploadBehaviorAllowedFileTypes],
    [] as string[]
  )

  const required = getSafely(settings, [ComponentSettingKey.FileUploadBehaviorRequired], false)
  const showHelpText = getSafely(settings, [ComponentSettingKey.FileUploadBehaviorShowHelpText], false)

  const defaultHelpText =
    acceptedFileTypes.length > 0
      ? `File type${acceptedFileTypes.length > 1 ? 's' : ''}: ${acceptedFileTypes.join(', ')}`
      : ''

  // ========================= form state =========================

  const value = formState?.[columnId] as File | null

  const fileUrl = useMemo(() => {
    if (!value) {
      return null
    }
    if (typeof value === 'string') {
      return value
    }
    if (isFile(value)) {
      return URL.createObjectURL(value)
    }
    return null
  }, [value])

  // Cleanup URL when component unmounts
  useEffect(() => {
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl)
      }
    }
  }, [fileUrl])

  useUpdateFormDefaultValue({
    defaultValue,
    value: fileUrl,
    onUpdate: (defValue) => {
      if (isValidUrl(defValue)) {
        fetchFileFromUrl(defValue).then((file) => {
          setFormState((prev) => ({ ...prev, [columnId]: file }))
        })
      } else {
        setFormState((prev) => ({ ...prev, [columnId]: null }))
      }
    },
  })

  const error = formErrors?.[columnId]

  const { fileExtension, fileSize } = getFileExtensionAndSize(value)
  const showFileMetadata = !!fileExtension && !!fileSize

  const [debouncedValue, setDebouncedValue] = useState<File | null>(value)
  const debounceTimeout = useRef<NodeJS.Timeout>()

  const handleChange = (file: File | null) => {
    if (!columnId) return

    // Clear previous errors immediately
    setFormErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[columnId]
      return newErrors
    })

    // Update form state immediately
    setFormState((prev) => ({ ...prev, [columnId]: file }))

    // Clear any existing timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }

    // Set a new timeout for validation
    debounceTimeout.current = setTimeout(() => {
      setDebouncedValue(file)
    }, 500)
  }

  const handleClear = () => {
    if (!columnId) return

    setFormState((prev) => ({ ...prev, [columnId]: null }))
    setDebouncedValue(null)
    setFormErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[columnId]
      return newErrors
    })
  }

  const validateField = useCallback(
    (fileToValidate: File | null) => {
      if (fileToValidate) {
        if (fileToValidate.size > maxSizeValue) {
          setFormErrors((prev) => ({ ...prev, [columnId]: 'File size exceeds the maximum allowed size' }))
          return
        }

        if (
          isFile(fileToValidate) &&
          acceptedFileTypes.length > 0 &&
          !validateFileTypes(fileToValidate, acceptedFileTypes)
        ) {
          setFormErrors((prev) => ({ ...prev, [columnId]: 'Invalid file type' }))
          return
        }
      }

      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[columnId]
        return newErrors
      })
    },
    [columnId, maxSizeValue, acceptedFileTypes, setFormErrors]
  )

  // Use effect for validation
  useEffect(() => {
    validateField(debouncedValue)
  }, [debouncedValue, validateField])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
        debounceTimeout.current = undefined
      }
    }
  }, [])

  // Add effect to update required field status
  useEffect(() => {
    if (required) {
      setRequiredFieldStatus(columnId, isEmpty(value))
    }
    return () => {
      if (required) {
        setRequiredFieldStatus(columnId, false)
      }
    }
  }, [required, value, columnId, setRequiredFieldStatus])

  const fileUploadProps: CompFileUploadProps = {
    required,
    label,
    error,
    accept: acceptedFileTypes.length > 0 ? acceptedFileTypes.join(',') : '*',
    value,
    onChange: handleChange,
    onClear: handleClear,
    supportingText: showHelpText ? helpText || defaultHelpText : undefined,
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(fileUploadProps)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(fileUploadProps)
}
